import { styled } from "@mui/material/styles";
import { Box, Typography, Button } from "@mui/material";
import { keyframes } from "@emotion/react";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translate(-50%, 60%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%);
    opacity: 1;
  }
`;

export const ModalOverlay = styled("div")(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1400,
  animation: `${fadeIn} 0.2s ease-out`,
  backdropFilter: "blur(4px)",
}));

export const ModalContainer = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(3),
  maxWidth: 400,
  width: "90%",
  position: "relative",
  zIndex: 1401,
  animation: `${slideUp} 0.3s ease-out`,
  boxShadow: theme.shadows[4],
  maxHeight: "90vh",
  overflowY: "auto",
}));

export const ModalContent = styled(Box)({
  padding: "20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  width: "100%",
});

export const ModalIcon = styled(Box)(({ theme, colorCode }) => ({
  width: 52,
  height: 52,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 24,
  marginBottom: 16,
  backgroundColor: colorCode ? colorCode : theme.palette.warning.light,
  color: colorCode ? "#ffffff" : theme.palette.warning.main,
  border: `2px solid ${theme.palette.warning.main}30`,
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shorter,
  }),
  "&:hover": {
    transform: "scale(1.05)",
  },
}));

export const ModalTitle = styled(Typography)(({ theme }) => ({
  margin: "0 0 8px 0",
  color: theme.palette.text.primary,
  fontSize: 18,
  fontWeight: 600,
  letterSpacing: "-0.01em",
}));

export const ModalMessage = styled(Typography)(({ theme }) => ({
  margin: "0 0 20px 0",
  color: theme.palette.text.secondary,
  fontSize: 13,
  lineHeight: 1.5,
  maxWidth: "100%",
  wordBreak: "break-word",
  overflowWrap: "break-word",
}));

export const ModalActions = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "center",
  width: "100%",
  flexWrap: "wrap",
}));

export const ModalButton = styled(Button)(({ theme, variant, colorCode }) => ({
  padding: "10px 20px",
  borderRadius: theme.shape.borderRadius,
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  minWidth: 100,
  maxWidth: "100%",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  ...(variant === "contained" && {
    backgroundColor: colorCode ? colorCode : theme.palette.error.main,
    color: theme.palette.common.white,
    boxShadow: colorCode
      ? `0 2px 8px ${colorCode}40`
      : `0 2px 8px ${theme.palette.error.main}40`,
    "&:hover": {
      backgroundColor: colorCode ? colorCode : theme.palette.error.dark,
      transform: "translateY(-1px)",
      boxShadow: colorCode
        ? `0 4px 12px ${colorCode}40`
        : `0 4px 12px ${theme.palette.error.main}40`,
    },
    "&:active": {
      transform: "translateY(0)",
      boxShadow: `0 2px 8px ${theme.palette.error.main}40`,
    },
  }),
  ...(variant === "outlined" && {
    border: `1px solid ${theme.palette.grey[300]}`,
    color: theme.palette.text.primary,
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  }),
}));
